import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Clock, Tag, ArrowRight, AlertCircle } from 'lucide-react';
import { supabase, safeSupabaseOperation } from '../../lib/supabase';
import type { BlogPost } from '../../types/database';

const BlogPreview: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredPost, setHoveredPost] = useState<string | null>(null);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setError(null);
      setLoading(true);

      // Default mock posts to use if Supabase connection fails
      const mockPosts: BlogPost[] = [
        {
          id: '1',
          title: 'La Révolution de la Mobilité Électrique en Afrique',
          slug: 'revolution-mobilite-electrique-afrique',
          excerpt: 'Découvrez comment les motos électriques transforment le paysage urbain africain, réduisent les émissions et créent de nouvelles opportunités économiques.',
          content: 'Contenu complet de l\'article...',
          cover_image: 'https://images.pexels.com/photos/3819830/pexels-photo-3819830.jpeg',
          category: 'Innovation',
          author: {
            name: 'Dr. Amadou Diallo',
            avatar: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg'
          },
          read_time: 8,
          created_at: new Date().toISOString()
        },
        {
          id: '2',
          title: 'Impact Environnemental des Motos Électriques',
          slug: 'impact-environnemental-motos-electriques',
          excerpt: 'Analyse détaillée de l\'impact environnemental des motos électriques : réduction des émissions de CO2, qualité de l\'air et développement durable.',
          content: 'Contenu complet de l\'article...',
          cover_image: 'https://images.pexels.com/photos/1108572/pexels-photo-1108572.jpeg',
          category: 'Environnement',
          author: {
            name: 'Dr. Fatou Ndiaye',
            avatar: 'https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg'
          },
          read_time: 10,
          created_at: new Date().toISOString()
        },
        {
          id: '3',
          title: 'Stations de Recharge Solaires : L\'Innovation au Service de l\'Autonomie',
          slug: 'stations-recharge-solaires-innovation-autonomie',
          excerpt: 'Les stations de recharge solaires Proxy Power révolutionnent l\'accès à l\'énergie propre en Afrique tout en créant des emplois durables.',
          content: 'Contenu complet de l\'article...',
          cover_image: 'https://images.pexels.com/photos/9800002/pexels-photo-9800002.jpeg',
          category: 'Technologie',
          author: {
            name: 'Marie Koné',
            avatar: 'https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg'
          },
          read_time: 12,
          created_at: new Date().toISOString()
        }
      ];

      // Use safe operation to handle network errors gracefully
      const { data, error, isOffline } = await safeSupabaseOperation(async () => {
        if (!supabase) throw new Error('Supabase not available');
        
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(3);

        if (error) throw error;
        return data;
      }, mockPosts);
      
      if (isOffline) {
        console.warn('Using mock blog posts due to network issues');
        setPosts(mockPosts);
      } else if (error) {
        throw error;
      } else {
        setPosts(data || mockPosts);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
      setError(error instanceof Error ? error.message : 'Failed to load blog posts');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (error) {
    return (
      <section className="py-24 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 text-red-600 mb-4">
              <AlertCircle size={24} />
              <p className="text-lg font-medium">Error loading blog posts</p>
            </div>
            <p className="text-neutral-600">{error}</p>
            <button
              onClick={() => loadPosts()}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_90%_10%,_rgba(24,153,78,0.05)_0%,_transparent_50%)]"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-4 sm:mb-5 md:mb-6 bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
            Dernières Actualités
          </h2>
          <p className="text-base sm:text-lg text-neutral-600 leading-relaxed px-4">
            Découvrez nos derniers articles sur la mobilité électrique, les innovations technologiques et nos engagements pour un avenir durable.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                  <div className="h-8 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {posts.map((post) => (
              <article 
                key={post.id} 
                className="bg-white rounded-xl shadow-lg overflow-hidden transition-all hover:-translate-y-2 hover:shadow-xl group"
                onMouseEnter={() => setHoveredPost(post.id)}
                onMouseLeave={() => setHoveredPost(null)}
              >
                <Link to={`/blog/${post.slug}`} className="block">
                  <div className="relative overflow-hidden">
                    <img
                      src={post.cover_image}
                      alt={post.title}
                      className={`w-full h-48 object-cover transition-transform duration-700 ${hoveredPost === post.id ? 'scale-110' : 'scale-100'}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                      <span className="flex items-center">
                        <Calendar size={16} className="mr-1" />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="flex items-center">
                        <Clock size={16} className="mr-1" />
                        {post.read_time} min
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center text-primary-600 font-medium group-hover:translate-x-1 transition-transform">
                      Lire la suite
                      <ArrowRight size={16} className="ml-1" />
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <Link
            to="/blog"
            className="inline-flex items-center px-8 py-4 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 transition-colors shadow-lg hover:shadow-xl transform hover:translate-y-[-2px]"
          >
            Voir tous les articles
            <ArrowRight size={20} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;